import React, { useContext, useEffect, useState } from 'react';
import { AppView, AppViewProps } from './App.view';
import { useUserData } from '../../hooks/useUserData';
import { useAuth } from '../../hooks/useAuth';
import { AuthContextEnum } from '../../constants/auth';
import { useReactiveVar } from '@apollo/client';
import { Navigate, useLocation } from 'react-router-dom';
import { AppContext, changeSelectedWorkspaceId, socialOAuthVar } from '../../providers/AppProvider';
import { useGlobalErrorLogging } from '../../hooks/useGlobalErrorLogging';
import { useHotjar } from '../../hooks/useHotjar';
import { useChromeExtension } from '../../hooks/useChromeExtension';
import { useSubscriptionStatus } from 'hooks/useSubscriptionStatus';
import { Network, SocialOAuthProvider } from '../../models/graphql';
import { useDidMount } from 'hooks/useDidMount';
import usePolling from 'hooks/usePolling';
import { GetMe_me_organizations } from '../../models/legacy-graphql';
import { GlobalErrorModalContext } from '../../providers/GlobalErrorModalProvider';

const profilesRedirectAuthContexts = [
  AuthContextEnum.connectProfile,
  AuthContextEnum.reconnectProfile,
  AuthContextEnum.connectCompany,
  AuthContextEnum.connectBusiness,
  AuthContextEnum.connectGroup,
  AuthContextEnum.toggleDirectPublishing,
  AuthContextEnum.getExternalId,
  AuthContextEnum.changeWhoPosts,
  AuthContextEnum.connectPage
];

const FIVE_MINUTES_IN_MS = 5 * 60 * 1000;

const validateSelectedWorkspace = (selectedWorkspaceId: string, organizations: GetMe_me_organizations[]): boolean => {
  let foundSelectedWorkspace = false;
  if (!selectedWorkspaceId) {
    return false;
  }
  organizations.forEach(org => {
    org.workspaces.forEach(workspace => {
      if (workspace.id === selectedWorkspaceId) {
        foundSelectedWorkspace = true;
      }
    });
  });

  return foundSelectedWorkspace;
};

export const AppController = (): JSX.Element => {
  const [workspaceValidationRunning, setWorkspaceValidationRunning] = useState(true);
  const location = useLocation();
  const { selectedWorkspaceId } = useContext(AppContext);
  const { isLoggedIn } = useAuth();
  const { userData, error: errorUserData, loading: loadingUserData, refetch: refetchUserData } = useUserData();
  const socialOAuth = useReactiveVar(socialOAuthVar);
  const { setError } = useContext(GlobalErrorModalContext);
  useGlobalErrorLogging();
  useHotjar();
  useChromeExtension();
  useSubscriptionStatus();
  const { startPolling: startUserDataPolling } = usePolling();
  let forcedRedirect = '';

  // Set global error if user data fails.
  useEffect(() => {
    if (isLoggedIn && errorUserData) {
      setError(errorUserData);
    }
  }, [errorUserData, isLoggedIn, setError]);

  // Validate the workspace for a logged in user, and if its not valid, switch the workspace.
  useEffect(() => {
    if (userData) {
      if (validateSelectedWorkspace(selectedWorkspaceId, userData.organizations)) {
        setWorkspaceValidationRunning(false);
      } else {
        if (userData.organizations.length === 1) {
          // If the user is only part of a single org, set the selected workspace to the first one.
          // Every org is guaranteed to have at least one workspace.
          changeSelectedWorkspaceId(userData.organizations[0].workspaces[0].id);
        } else {
          if (selectedWorkspaceId) {
            // If the user is apart of no orgs, or multiple, set the selected to null and let the app/router decide what to do.
            changeSelectedWorkspaceId('');
          } else {
            // At this point, selectedWorkspace is '' and the user has != 1 organization. Let the app continue so it
            // can show the org switcher or "no organizations" view.
            setWorkspaceValidationRunning(false);
          }
        }
      }
    }
  }, [userData, selectedWorkspaceId, location.pathname]);

  const viewProps: AppViewProps = {
    loadingUserData: isLoggedIn && (loadingUserData || workspaceValidationRunning)
  };

  useDidMount(() => {
    if (isLoggedIn && userData) {
      // We are polling the user data so that the we update the payment status info without refreshing the page
      startUserDataPolling(() => refetchUserData(), FIVE_MINUTES_IN_MS);
    }
  });

  if (
    isLoggedIn &&
    !selectedWorkspaceId &&
    userData?.organizations &&
    !window.location.pathname?.includes('/invite')
    // ^^ This prevents bug when logged user visiting /invite/:token with no orgs is being redirected to /no-org
  ) {
    if (userData.organizations && userData.organizations.length > 1) {
      forcedRedirect = '/change-org';
    } else if (userData.organizations && !userData.organizations.length) {
      // TODO Verify what to do when we have no organizations.
      forcedRedirect = '/no-org';
    }
  }

  // check if socialOAuth has been set and handle appropriate redirects
  if (socialOAuth) {
    if (
      socialOAuth.context === AuthContextEnum.signup &&
      (socialOAuth.network === Network.FACEBOOK || socialOAuth.network === Network.GOOGLE) &&
      !isLoggedIn
    ) {
      forcedRedirect = '/signup';
    }
    // ^^^
    // We don't need to check social auth against login, since it will be redirected to login anyways, and once login sets
    // socialOAuth var to null, session storage will be cleaned up
    if (profilesRedirectAuthContexts.includes(socialOAuth.context)) {
      if (socialOAuth.oAuthProvider === SocialOAuthProvider.BITLY) {
        forcedRedirect = '/settings/tracking';
      } else {
        forcedRedirect = '/settings/profiles';
      }
    }
  }

  if (forcedRedirect) {
    // Make sure we're not on the route already, and if we aren't, make sure we're not supposed to be on the login
    // form because we were routed there from a protected page.
    if (location.pathname !== forcedRedirect && location.pathname !== '/login') {
      return <Navigate to={forcedRedirect} replace />;
    }
  }

  return <AppView {...viewProps} />;
};
